import {
    EDxfEntityKind,
    EDxfHatchPattern,
    EDxfHorizontalAlign,
    EDxfVerticalAlign,
    IDxfArc,
    IDxfEntity,
    IDxfHatch,
    IDxfLine,
    IDxfPoint,
    IDxfPolyline,
    IDxfText,
    IDxfVertex,
} from './dxf-entities';
import { EDxfSheetLayer } from './dxf-layers';
import { IDxfOut, coord, int, pair, point, point2d, real, takeHandle } from './dxf-out';
import { IDxfViewport } from './dxf-sheet';
import { DXF_TEXT_STYLE } from './dxf-tables';

/** Decimal places of a vertex bulge: a ratio, not a length. */
const BULGE_FACTOR: number = 10 ** 10;
/** LWPOLYLINE flag 128: the linetype runs on across the vertices, so a dashed outline stays dashed. */
const PLINE_GENERATION: number = 128;
/** ANSI31 at scale 1: lines at 45°, the next one offset by (−2.245, 2.245) — 3.175 apart. */
const ANSI31_OFFSET: number = 2.2450640303;
/** VIEWPORT flags 90: always set (32768) and zoom locked (16384) — the view keeps its scale. */
const VIEWPORT_FLAGS: number = 32768 + 16384;
/** The paper viewport every layout has, id 1: always set, adaptive grid, UCS icon shown. */
const PAPER_VIEWPORT_FLAGS: number = 557088;
/** The paper viewport reaches this much past the sheet. */
const PAPER_VIEW_MARGIN: number = 1.1;

/** Which space an entity is written to and the block record that owns it there. */
export interface IDxfSpace {
    owner: string;
    paper: boolean;
}

function beginEntity(out: IDxfOut, type: string, layer: string, space: IDxfSpace): void {
    pair(out, 0, type);
    pair(out, 5, takeHandle(out));
    pair(out, 330, space.owner);
    pair(out, 100, 'AcDbEntity');
    if (space.paper) {
        int(out, 67, 1);
    }
    pair(out, 8, layer);
}

/** Group 48 for a line that may be dashed: its pattern is drawn at the view's scale. */
function linetypeScale(out: IDxfOut, scale: number): void {
    if (scale !== 1) {
        real(out, 48, scale);
    }
}

function writeLine(out: IDxfOut, entity: IDxfLine): void {
    linetypeScale(out, entity.ltScale);
    pair(out, 100, 'AcDbLine');
    point(out, 10, entity.from);
    point(out, 11, entity.to);
}

function writePolyline(out: IDxfOut, entity: IDxfPolyline): void {
    linetypeScale(out, entity.ltScale);
    pair(out, 100, 'AcDbPolyline');
    int(out, 90, entity.vertices.length);
    int(out, 70, (entity.closed ? 1 : 0) + PLINE_GENERATION);
    coord(out, 43, 0);
    entity.vertices.forEach((vertex: IDxfVertex): void => {
        point2d(out, 10, vertex.at);
        if (vertex.bulge !== 0) {
            pair(out, 42, String(Math.round(vertex.bulge * BULGE_FACTOR) / BULGE_FACTOR));
        }
    });
}

function writeArc(out: IDxfOut, entity: IDxfArc): void {
    linetypeScale(out, entity.ltScale);
    pair(out, 100, 'AcDbCircle');
    point(out, 10, entity.center);
    coord(out, 40, entity.radius);
    pair(out, 100, 'AcDbArc');
    real(out, 50, entity.startDeg);
    real(out, 51, entity.endDeg);
}

function writeText(out: IDxfOut, entity: IDxfText): void {
    pair(out, 100, 'AcDbText');
    point(out, 10, entity.at);
    coord(out, 40, entity.height);
    pair(out, 1, entity.text);
    real(out, 50, entity.rotationDeg);
    if (entity.widthFactor !== 1) {
        real(out, 41, entity.widthFactor);
    }
    pair(out, 7, DXF_TEXT_STYLE);
    int(out, 72, entity.hAlign);
    // A left-baseline text is placed by its first point; any other justification by the second.
    if (entity.hAlign !== EDxfHorizontalAlign.Left || entity.vAlign !== EDxfVerticalAlign.Baseline) {
        point(out, 11, entity.at);
    }
    pair(out, 100, 'AcDbText');
    int(out, 73, entity.vAlign);
}

function writeHatchPattern(out: IDxfOut, entity: IDxfHatch): void {
    real(out, 52, 0);
    real(out, 41, entity.patternScale);
    int(out, 77, 0);
    int(out, 78, 1);
    real(out, 53, 45);
    coord(out, 43, 0);
    coord(out, 44, 0);
    real(out, 45, -ANSI31_OFFSET * entity.patternScale);
    real(out, 46, ANSI31_OFFSET * entity.patternScale);
    int(out, 79, 0);
}

function writeHatch(out: IDxfOut, entity: IDxfHatch): void {
    const solid: boolean = entity.pattern === EDxfHatchPattern.Solid;
    pair(out, 100, 'AcDbHatch');
    point(out, 10, [0, 0]);
    real(out, 210, 0);
    real(out, 220, 0);
    real(out, 230, 1);
    pair(out, 2, entity.pattern);
    int(out, 70, solid ? 1 : 0);
    int(out, 71, 0);
    int(out, 91, 1);
    // One boundary path: external (1) and a polyline (2), closed, no bulges.
    int(out, 92, 3);
    int(out, 72, 0);
    int(out, 73, 1);
    int(out, 93, entity.boundary.length);
    for (const at of entity.boundary) {
        point2d(out, 10, at);
    }
    int(out, 97, 0);
    int(out, 75, solid ? 0 : 1);
    int(out, 76, 1);
    if (!solid) {
        writeHatchPattern(out, entity);
    }
    int(out, 98, 0);
}

/** One entity, owned by the space it is written to. */
export function writeEntity(out: IDxfOut, entity: IDxfEntity, space: IDxfSpace): void {
    beginEntity(out, entity.kind, entity.layer, space);
    switch (entity.kind) {
        case EDxfEntityKind.Line:
            writeLine(out, entity);
            break;
        case EDxfEntityKind.Polyline:
            writePolyline(out, entity);
            break;
        case EDxfEntityKind.Arc:
            writeArc(out, entity);
            break;
        case EDxfEntityKind.Text:
            writeText(out, entity);
            break;
        case EDxfEntityKind.Hatch:
            writeHatch(out, entity);
            break;
    }
}

/** A VIEWPORT: `id` 1 is the layout's own paper view, the rest show model space. Returns its handle. */
function writeViewport(out: IDxfOut, viewport: IDxfViewport, id: number, space: IDxfSpace): string {
    pair(out, 0, 'VIEWPORT');
    const handle: string = takeHandle(out);
    pair(out, 5, handle);
    pair(out, 330, space.owner);
    pair(out, 100, 'AcDbEntity');
    int(out, 67, 1);
    pair(out, 8, EDxfSheetLayer.Viewports);
    pair(out, 100, 'AcDbViewport');
    point(out, 10, viewport.center);
    coord(out, 40, viewport.width);
    coord(out, 41, viewport.height);
    int(out, 68, id);
    int(out, 69, id);
    point2d(out, 12, viewport.viewCenter);
    point2d(out, 13, [0, 0]);
    point2d(out, 14, [10, 10]);
    point2d(out, 15, [10, 10]);
    point(out, 16, [0, 0]);
    real(out, 36, 1);
    point(out, 17, [0, 0]);
    real(out, 42, 50);
    real(out, 43, 0);
    real(out, 44, 0);
    coord(out, 45, viewport.viewHeight);
    real(out, 50, 0);
    real(out, 51, 0);
    int(out, 72, 100);
    int(out, 90, id === 1 ? PAPER_VIEWPORT_FLAGS : VIEWPORT_FLAGS);
    pair(out, 1, '');
    int(out, 281, 0);
    int(out, 71, 0);
    int(out, 74, 0);
    point(out, 110, [0, 0]);
    point(out, 111, [1, 0]);
    point(out, 112, [0, 1]);
    int(out, 79, 0);
    real(out, 146, 0);
    int(out, 282, 0);
    return handle;
}

/**
 * The viewports of the sheet: first the paper view of the layout, then one per view. Returns the
 * handle of the paper view, which the layout names as its last active viewport.
 */
export function writeViewports(
    out: IDxfOut,
    paper: { width: number; height: number },
    viewports: readonly IDxfViewport[],
    space: IDxfSpace
): string {
    const center: IDxfPoint = [paper.width / 2, paper.height / 2];
    const main: string = writeViewport(
        out,
        {
            center,
            width: paper.width * PAPER_VIEW_MARGIN,
            height: paper.height * PAPER_VIEW_MARGIN,
            viewCenter: center,
            viewHeight: paper.height * PAPER_VIEW_MARGIN,
        },
        1,
        space
    );
    viewports.forEach((viewport: IDxfViewport, index: number): void => {
        writeViewport(out, viewport, index + 2, space);
    });
    return main;
}
